import { api } from "@/lib/api";
import { CartItem, CartResponse } from "@/types";
import { notifications } from "@mantine/notifications";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../Auth";
import { useHub } from "../Hub";

interface CartContextType {
    items: CartItem[];
    total: number;
    itemCount: number;
    loading: boolean;
    fetchCart: () => Promise<void>;
    addToCart: (eventId: number) => Promise<boolean>;
    removeItem: (cartItemId: number) => Promise<void>;
    clearCart: () => Promise<void>;
    clearLocalCart: () => void;
    isInCart: (documentNumber: string, county: string) => boolean;
}

const CartContext = createContext<CartContextType | null>(null);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const { currentUser } = useAuth();
    const { hubCartItems, hubCartTotal, clearHubCart } = useHub();

    // Handle real-time cart updates from SignalR hub
    useEffect(() => {
        if (hubCartItems !== null && hubCartTotal !== null) {
            // console.log("Cart: Received SignalR update", { items: hubCartItems.length, total: hubCartTotal });
            setItems(hubCartItems);
            setTotal(hubCartTotal);
            clearHubCart();
        }
    }, [hubCartItems, hubCartTotal, clearHubCart]);

    const fetchCart = useCallback(async () => {
        if (!currentUser) {
            setItems([]);
            setTotal(0);
            return;
        }

        try {
            // TODO: Consider using cart state management to avoid refetching too often
            setLoading(true);
            const response: CartResponse = await api.get("/cart");
            setItems(response.items);
            setTotal(response.total);
        } catch (error) {
            console.error("Failed to fetch cart:", error);
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    const removeItem = async (cartItemId: number) => {
        try {
            setLoading(true);
            const response: CartResponse = await api.delete(`/cart/${cartItemId}`);
            setItems(response.items);
            setTotal(response.total);
        } catch (error) {
            console.error("Failed to remove item:", error);
            notifications.show({
                title: "Error",
                message: "Failed to remove item from cart",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const clearCart = async () => {
        try {
            setLoading(true);
            const response: CartResponse = await api.delete("/cart");
            setItems(response.items);
            setTotal(response.total);
        } catch (error) {
            console.error("Failed to clear cart:", error);
            notifications.show({
                title: "Error",
                message: "Failed to clear cart",
                color: "red",
                position: "top-center",
                "aria-live": "polite",
                closeButtonProps:{
                    "aria-label": "Close error notification"
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const clearLocalCart = useCallback(() => {
        setItems([]);
        setTotal(0);
    }, []);

    const addToCart = async (eventId: number): Promise<boolean> => {
        try {
            setLoading(true);
            const response: CartResponse = await api.post("/cart", { eventId });
            setItems(response.items);
            setTotal(response.total);
            return true;
        } catch (error) {
            console.error("Failed to add to cart:", error);
            // Error notification is handled by the API interceptor
            return false;
        } finally {
            setLoading(false);
        }
    };

    const isInCart = useCallback((documentNumber: string, county: string): boolean => {
        return items.some(
            (item) => item.documentNumber === documentNumber && item.county === county
        );
    }, [items]);

    const value: CartContextType = {
        items,
        total,
        itemCount: items.length,
        loading,
        fetchCart,
        addToCart,
        removeItem,
        clearCart,
        clearLocalCart,
        isInCart,
    };

    return (
        <CartContext.Provider value={value}>{children}</CartContext.Provider>
    );
};
// Let's separate the hook/context from the provider.
// This warning means that the state isn't preserved during refreshes.
// (This means that we need to refetch whenever the component mounts.)
export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
};
